import type { Bbox } from "./where-bounding-boxes";

/** Travily region slugs, matching the indexed `region` keyword (World::Values::Regions)
 *  and the region suggestion ids in `getDefaultWhereSuggestions()`. */
export const REGION_SLUGS = [
  "north-america",
  "latin-america",
  "caribbean",
  "europe",
  "africa",
  "middle-east",
  "asia",
  "oceania",
] as const;

export type RegionSlug = (typeof REGION_SLUGS)[number];

/**
 * Deliberately generous extents that overlap each other — a box is never the
 * definition of a region. On a term-filtering surface (destinations) it only
 * frames the map viewport while the indexed `region` term (World::Values::Regions)
 * does the filtering, so outlying territories (Guam under oceania, French Guiana
 * under latin-america) are filtered in while sitting outside their frame. On a
 * surface with no `region` field (programs) the box IS the geo filter, as it was
 * before the taxonomy, so these must stay wide enough to contain the region.
 * Tuples are `[west, south, east, north]` per the Mapbox v6 bbox convention.
 */
export const REGION_BBOXES: Readonly<Record<RegionSlug, Bbox>> = {
  "north-america": [-168, 12, -52, 84],
  "latin-america": [-118, -56, -34, 33],
  caribbean: [-90, 8, -58, 28],
  europe: [-25, 34, 45, 71],
  africa: [-20, -35, 55, 38],
  "middle-east": [25, 12, 64, 43],
  asia: [25, -11, 180, 81],
  oceania: [110, -50, 180, 0],
};

/** A term-based narrowing of the Where pill: a region suggestion or a country-scale pick. */
export type WhereFilter = { kind: "region"; slug: RegionSlug } | { kind: "country"; code: string };

export function isRegionSlug(value: string | null | undefined): value is RegionSlug {
  return !!value && (REGION_SLUGS as readonly string[]).includes(value);
}

/** Searchkit `facetFilters` form (`"attribute:value"`). The attribute must exist in the
 *  consuming index's `filter_attributes`/`facet_attributes` or Searchkit throws. */
export function whereFilterToFacetFilters(filter: WhereFilter | null): string[] | undefined {
  if (!filter) return undefined;
  return filter.kind === "region" ? [`region:${filter.slug}`] : [`country_code:${filter.code}`];
}

/** The same predicate as a raw OpenSearch clause, for the map-pins msearch body. */
export function whereFilterToTermsFilter(filter: WhereFilter | null): Record<string, unknown> | null {
  if (!filter) return null;
  return filter.kind === "region" ? { terms: { region: [filter.slug] } } : { terms: { country_code: [filter.code] } };
}

/** Stable dependency key for effects that must not re-run on object identity churn. */
export function whereFilterKey(filter: WhereFilter | null): string | null {
  if (!filter) return null;
  return filter.kind === "region" ? `region:${filter.slug}` : `country:${filter.code}`;
}

/** A country-scale pick filters by `country_code` rather than its bbox — Indonesia's box
 *  swallows Malaysia and Singapore. Region and place picks keep their bbox. */
export function whereFilterForSuggestion(suggestion: {
  feature_type?: string | null;
  country_code?: string | null;
}): WhereFilter | null {
  if (suggestion.feature_type !== "country" || !suggestion.country_code) return null;
  return { kind: "country", code: suggestion.country_code.toUpperCase() };
}
